import React from 'react'
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    ModalOverlayProps,
    ModalProps,
} from '@chakra-ui/react'
import { ChevronLeftIcon, CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import {
    Button,
    IconButton,
    Spacer,
    useBreakpointValue,
} from '@chakra-ui/react'

import {
    AppointmentDailyHuddleFieldsFragment,
    RescheduleFieldsFragment,
} from '~graphql/generated/graphql'
import AppointmentDetails from '~components/appointments/detailed-appointment/details'
import { Link } from 'gatsby'
import { UserRoutes } from '~config/routes'
import ClickToCopyText from './click-to-copy'
import { getPortalLink, getReviewLink } from '~utils/helpers'
import AdminIdButton from '~components/shared/buttons/admin-id-button'
import { SupabaseEntities } from '~config/constants'
import ChangeLogButton from '~components/shared/modals/change-log/change-log-button'

interface Props extends Omit<ModalProps, 'children'> {
    appointment: AppointmentDailyHuddleFieldsFragment | undefined
    reschedule?: RescheduleFieldsFragment
    overlayOverride?: ModalOverlayProps
}

const AppointmentDetailsDrawer: React.FC<Props> = ({
    appointment,
    reschedule,
    isOpen,
    onClose,
    overlayOverride,
    ...props
}) => {
    const modalSize = useBreakpointValue({ base: 'full', md: 'lg' })

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            blockScrollOnMount={false}
            size={modalSize}
            {...props}
        >
            <DrawerOverlay {...overlayOverride} />
            <DrawerContent>
                <DrawerHeader
                    pb={4}
                    shadow="blue.md"
                    display="flex"
                    justifyContent="space-between"
                    gap={3}
                >
                    <IconButton
                        size="sm"
                        icon={<ChevronLeftIcon fontSize="xl" />}
                        variant="outline"
                        rounded="full"
                        aria-label="Close Modal"
                        onClick={onClose}
                    />
                    <Spacer />
                    {appointment && (
                        <>
                            <ChangeLogButton entityId={appointment.id} />
                            <AdminIdButton
                                entity={SupabaseEntities.Appointment}
                                id={appointment.id}
                                extraCopyOptions={[
                                    {
                                        label: 'Appointment PMS ID',
                                        value: appointment.pms_id,
                                    },
                                    {
                                        label: 'Client ID',
                                        value: appointment.client?.id,
                                    },
                                    {
                                        label: 'Patient ID',
                                        value: appointment.patient?.id,
                                    },
                                    {
                                        label: 'Clinic Appt Status ID',
                                        value: appointment.clinic_appt_status?.id,
                                    },
                                    {
                                        label: 'Service ID',
                                        value: appointment.service?.id,
                                    },
                                    {
                                        label: 'Clinician ID',
                                        value: appointment.clinician?.id,
                                    },
                                    {
                                        label: 'Room ID',
                                        value: appointment.room?.id,
                                    },
                                ]}
                                type="icon"
                            />

                            <Button
                                size="sm"
                                variant="outline"
                                rounded="full"
                                rightIcon={<CopyIcon fontSize="lg" />}
                            >
                                <ClickToCopyText
                                    copyOverride={getReviewLink(appointment)}
                                    _hover={{}}
                                >
                                    Copy Review Link
                                </ClickToCopyText>
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                rounded="full"
                                rightIcon={<CopyIcon fontSize="lg" />}
                            >
                                <ClickToCopyText
                                    copyOverride={getPortalLink(appointment)}
                                    _hover={{}}
                                >
                                    Copy Portal Link
                                </ClickToCopyText>
                            </Button>
                        </>
                    )}
                    <Link
                        to={UserRoutes.AppointmentDetails(
                            appointment?.id || '',
                        )}
                    >
                        <Button
                            size="sm"
                            rightIcon={<ExternalLinkIcon fontSize="lg" />}
                            variant="outline"
                            rounded="full"
                        >
                            View all details
                        </Button>
                    </Link>
                </DrawerHeader>
                <DrawerBody px={{ base: 2, md: 6 }} py={{ base: 4, md: 6 }}>
                    {appointment && (
                        <AppointmentDetails
                            appointment={appointment}
                            reschedule={reschedule}
                        />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

export default AppointmentDetailsDrawer
